import { useSendMerchantAccessMutation } from "Api/Mutations/Merchants/useSendMerchantAccessMutation";
import {
  Resources,
  useResource,
  getResourcePath,
} from "Translations/Resources";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { Trans } from "react-i18next";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "Utils/Storage/SessionStorage/SecureSessionStorage";
import { SessionStorageKeys } from "Utils/Storage/SessionStorage/SessionStorageKeys";

const RESOURCES = Resources.Hunter.Merchants.Detail;
const EMAIL_SENT_STORAGE_VALUE = "true";

const getStorageKey = (merchantUserLogin: string, merchantPublicID: string) => {
  return `${SessionStorageKeys.MerchantAccessSent}-${merchantPublicID}-${merchantUserLogin}`;
};

type Props = {
  merchantUserLogin: string;
  merchantPublicID: string;
};

export const ResendAccessButton = ({
  merchantUserLogin,
  merchantPublicID,
}: Props) => {
  const { t } = useResource();
  const storageKey = getStorageKey(merchantUserLogin, merchantPublicID);

  const { mutate, isPending } = useSendMerchantAccessMutation({
    isMerchantQueryInvalidationDisabled: true,
    onSuccess: () => {
      setSessionStorageItem(storageKey, EMAIL_SENT_STORAGE_VALUE);
    },
  });

  const handleResendAccess = () =>
    isPending
      ? undefined
      : mutate({ data: { login: merchantUserLogin }, merchantPublicID });

  const wasEmailSent =
    getSessionStorageItem(storageKey) === EMAIL_SENT_STORAGE_VALUE;

  return (
    <Box display="flex" justifyContent="center" height={40}>
      {wasEmailSent ? (
        <Typography
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1}
          flexWrap="wrap"
          color="success.main"
        >
          <MarkEmailReadIcon />
          <Trans
            i18nKey={getResourcePath(RESOURCES.ResendAccessSuccessWithUser)}
            values={{ userLogin: merchantUserLogin }}
            components={{ bold: <strong /> }}
          />
        </Typography>
      ) : (
        <Button
          variant="outlined"
          size="small"
          onClick={handleResendAccess}
          endIcon={
            isPending ? <CircularProgress size={16} /> : <ForwardToInboxIcon />
          }
        >
          {t(RESOURCES.ResendAccessButton)}
        </Button>
      )}
    </Box>
  );
};
